import { useState, memo, useCallback, useMemo, useEffect } from 'react';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
// @mui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog, { dialogClasses } from '@mui/material/Dialog';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useEventListener } from 'src/hooks/use-event-listener';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useRouter } from 'src/routes/hooks';
import SearchNotFound from 'src/components/search-not-found';
//
import ResultItem from './result-item';
import { useNavData } from '../../dashboard/config-navigation';
import { applyFilter, groupedData, getAllItems } from './utils';

// ----------------------------------------------------------------------

function Searchbar() {
  const theme = useTheme();

  const router = useRouter();

  const search = useBoolean();

  const lgUp = useResponsive('up', 'lg');

  const [searchQuery, setSearchQuery] = useState('');

  const [selectedIndex, setSelectedIndex] = useState(0);

  const { user } = useAuthContext();

  const getUserRole = () => {
    if (user?.is_admin) return 'admin';
    if (user?.is_academic) return 'academic';
    if (user?.is_non_academic) return 'non_academic';
    if (user?.is_student) return 'student';
    if (user?.is_parent) return 'parent';
    if (user?.is_height_student) return 'height_student';
    if (user?.is_outsider) return 'outsider';
    return null;
  };

  const userRole = getUserRole();

  const navData = useNavData();

  const handleClose = useCallback(() => {
    search.onFalse();
    setSearchQuery('');
    setSelectedIndex(0);
  }, [search]);

  const handleKeyDown = (event) => {
    if ((event.key === 'k' || event.key === 'K') && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      search.onToggle();
      setSearchQuery('');
      setSelectedIndex(0);
    }
  };

  useEventListener('keydown', handleKeyDown);

  const handleClick = useCallback(
    (path) => {
      if (!path) return;
      if (path.includes('http')) {
        window.open(path);
      } else {
        router.push(path);
      }
      handleClose();
    },
    [handleClose, router]
  );

  const handleSearch = useCallback((event) => {
    setSearchQuery(event.target.value);
    setSelectedIndex(0);
  }, []);

  const allItems = useMemo(
    () => getAllItems({ data: navData, userRole }),
    [navData, userRole]
  );

  const dataFiltered = useMemo(
    () =>
      applyFilter({
        inputData: allItems,
        query: searchQuery,
      }),
    [allItems, searchQuery]
  );

  const notFound = Boolean(searchQuery && !dataFiltered.length);

  const handleInputKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev < dataFiltered.length - 1 ? prev + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : dataFiltered.length - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (dataFiltered.length > 0) {
        const itemToOpen = dataFiltered[selectedIndex] || dataFiltered[0];
        if (itemToOpen?.path) {
          handleClick(itemToOpen.path);
        }
      }
    } else if (event.key === 'Escape') {
      handleClose();
    }
  };

  // Keep selected index within bounds
  useEffect(() => {
    if (selectedIndex >= dataFiltered.length) {
      setSelectedIndex(0);
    }
  }, [dataFiltered.length, selectedIndex]);

  const renderItems = () => {
    const data = groupedData(dataFiltered);
    let itemGlobalIndex = 0;

    return Object.keys(data).map((group, index) => (
      <List key={group || index} disablePadding>
        {data[group].map((item) => {
          const { title, path } = item;
          const currentIndex = itemGlobalIndex++;
          const isSelected = currentIndex === selectedIndex;

          const partsTitle = parse(title, match(title, searchQuery));

          const partsPath = parse(path, match(path, searchQuery));

          return (
            <ResultItem
              path={partsPath}
              title={partsTitle}
              key={`${title}-${path}-${group}-${currentIndex}`}
              groupLabel={group}
              selected={isSelected}
              onClickItem={() => handleClick(path)}
            />
          );
        })}
      </List>
    ));
  };

  const renderButton = (
    <Stack
      direction="row"
      alignItems="center"
      onClick={search.onTrue}
      sx={{ cursor: 'pointer' }}
    >
      <IconButton onClick={search.onTrue}>
        <Iconify icon="eva:search-fill" />
      </IconButton>

      {lgUp && (
        <Label sx={{ px: 0.75, fontSize: 12, color: 'text.secondary', cursor: 'pointer' }}>
          ⌘K
        </Label>
      )}
    </Stack>
  );

  return (
    <>
      {renderButton}

      <Dialog
        fullWidth
        maxWidth="sm"
        open={search.value}
        onClose={handleClose}
        transitionDuration={{
          enter: theme.transitions.duration.shortest,
          exit: 0,
        }}
        PaperProps={{
          sx: {
            mt: 15,
            overflow: 'unset',
          },
        }}
        sx={{
          [`& .${dialogClasses.container}`]: {
            alignItems: 'flex-start',
          },
        }}
      >
        <Box sx={{ p: 3, borderBottom: `solid 1px ${theme.palette.divider}` }}>
          <InputBase
            fullWidth
            autoFocus
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearch}
            onKeyDown={handleInputKeyDown}
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" width={24} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            endAdornment={
              <Label
                onClick={handleClose}
                sx={{ letterSpacing: 1, color: 'text.secondary', cursor: 'pointer' }}
              >
                esc
              </Label>
            }
            inputProps={{
              sx: { typography: 'h6' },
            }}
          />
        </Box>

        <Scrollbar sx={{ p: 3, pt: 2, height: 400 }}>
          {notFound ? <SearchNotFound query={searchQuery} sx={{ py: 10 }} /> : renderItems()}
        </Scrollbar>
      </Dialog>
    </>
  );
}

export default memo(Searchbar);
